<template>
  <div class="withdraw_form_wrapper">
    <div class="content_withdraw_title">
      <h3 class="h3">{{ isNjPointWithdraw ? $t('global.subTitlePoint') : $t('global.subTitle') }}</h3>
      <div class="point_per_currency_wrapper">
        <p class="point_per_currency">
          {{
            isNjPointWithdraw
              ? $t('global.exchangePointRate', {
                  unitValue: 1,
                  unit: 'P',
                  currency: 'P',
                })
              : $t('global.exchangeRate', {
                  unitValue: exchangeRate,
                  unit: pointInfo.pointUnitName,
                  currency: exchangeCurrency.currency,
                })
          }}
        </p>
      </div>
    </div>
    <hr class="solid" />
    <div class="content_withdraw_form">
      <div class="content_withdraw_form_title">
        {{ $t('global.available', { pointName: pointInfo.pointName }) }}
      </div>
      <div class="content_withdraw_own_point">
        <span class="content_withdraw_own_point_value">
          {{ addComma(pointInfo.point) }}
        </span>
        <span class="content_withdraw_own_point_title">
          {{ $t('gnb.menu.point') }}
        </span>
      </div>
    </div>
    <div class="content_withdraw_point_form">
      <div class="content_withdraw_form_title">
        {{ isNjPointWithdraw ? $t('global.withdrawNjPointInput') : $t('global.withdrawPointInput') }}
      </div>
      <div class="content_withdraw_own_point">
        <div class="input_wrapper">
          <v-button class="btn04" @handleClick="setPointToMax" :text="$t('global.maxAmount')" />
          <div class="point_input">
            <v-input-comma
              v-model="point"
              :maxlength="10"
              :placeholder="
                $t('global.inputPlaceholder', {
                  point: this.exchangeCurrency.exchangeAmountUnit,
                  unit: pointInfo.pointUnitName,
                })
              "
            />
            <div class="small_title">
              {{ $t('gnb.menu.point') }}
            </div>
          </div>
        </div>
        <span class="content_withdraw_own_point_title">
          {{ $t('gnb.menu.point') }}
        </span>
      </div>
    </div>
    <p class="warning">{{ warningMessageValidatePoint() }}</p>
    <div class="withdraw_box">
      <div>
        <p class="withdraw_box_title">
          {{ isNjPointWithdraw ? $t('global.withdrawAmountPoint') : $t('global.withdrawAmount') }}
        </p>
        <p class="withdraw_box_description">
          {{ isNjPointWithdraw ? $t('global.transferFeeDescriptionPoint') : $t('global.transferFeeDescription') }}
        </p>
      </div>
      <p v-if="isNjPointWithdraw" class="withdraw_box_value">
        {{ !!warningMessageValidatePoint() ? '0' : this.addComma(currentWithdrawValue) }} {{ currencyMark }}
      </p>
      <p v-else class="withdraw_box_value">
        {{ currencyMark }}{{ !!warningMessageValidatePoint() ? '0' : this.addComma(currentWithdrawValue) }}
      </p>
    </div>
    <div class="checkbox_wrapper">
      <check-box
        @custom-checked="handleCheck(1)"
        :checked="agreeCheck"
        id="agree"
        :text="isNjPointWithdraw ? $t('global.confirmWithdrawCautionPoint') : $t('global.confirmWithdrawCaution')"
      />
    </div>
    <div class="caution">
      <p>{{ isNjPointWithdraw ? $t('global.pointCaution') : $t('global.withdrawCaution') }}</p>
      <div v-if="isNjPointWithdraw" class="caution_description">
        -
        {{
          $t('global.withdrawPointCautionDescriptions.1', {
            min: this.addComma(exchangeCurrency.exchangeMinAmount / exchangeRate),
            max: this.addComma(exchangeCurrency.exchangeMaxAmount / exchangeRate),
            currencyMark,
            currency: exchangeCurrency.currency,
          })
        }}<br />
        - {{ $t('global.withdrawPointCautionDescriptions.2') }}<br />
        - {{ $t('global.withdrawPointCautionDescriptions.3') }}<br />
        - {{ $t('global.withdrawPointCautionDescriptions.4') }}<br />
      </div>
      <div v-else class="caution_description">
        -
        {{
          $t('global.withdrawCautionDescriptions.1', {
            min: this.addComma(exchangeCurrency.exchangeMinAmount / exchangeRate),
            max: this.addComma(exchangeCurrency.exchangeMaxAmount / exchangeRate),
            currencyMark,
            currency: exchangeCurrency.currency,
          })
        }}<br />
        - {{ $t('global.withdrawCautionDescriptions.2') }}<br />
        - {{ $t('global.withdrawCautionDescriptions.3') }}<br />
        - {{ $t('global.withdrawCautionDescriptions.4') }}<br />
        - {{ $t('global.withdrawCautionDescriptions.5') }}<br />
        <p
          v-if="userRegion === 'jppc'"
          class="caution_description jppc"
          v-html="$t('global.withdrawCautionDescriptions.6')"
        />
      </div>
    </div>
    <hr class="solid" />
    <p class="caution_guide">
      {{ isNjPointWithdraw ? $t('global.nextStepForPoint.1') : $t('global.nextStep.1') }}<br />
      {{ isNjPointWithdraw ? $t('global.nextStepForPoint.2') : $t('global.nextStep.2') }}<br />
      <a :href="guideURL" target="_blank" class="caution_anchor">{{
        isNjPointWithdraw ? $t('global.nextStepForPoint.3') : $t('global.nextStep.3')
      }}</a>
    </p>
    <button class="btn btn01" @click="goToGlobalWithdraw" :disabled="!agreeCheck || !!warningMessageValidatePoint()">
      {{ isNjPointWithdraw ? $t('global.applyBtnPoint') : $t('common.next') }}
    </button>
  </div>
</template>

<script>
import CheckBox from '@/components/atoms/CheckBox';
import { mapGetters, mapActions } from 'vuex';
import { checkUserRegion } from '@/utils';
import VInputComma from '@/components/atoms/VInputComma';
import VButton from '@/components/atoms/VButton';
import mixin from '@/utils/mixin';
import { GLOBAL_GUIDE_URL } from '@/utils/variables';
import { exchangeApi } from '@/api';

export default {
  mixins: [mixin],
  components: {
    CheckBox,
    VInputComma,
    VButton,
  },
  data() {
    return {
      agreeCheck: false,
      withdrawPolicy: '',
      withdrawNotice: '',
      point: '',
    };
  },
  created() {
    this.init();
  },
  watch: {
    '$i18n.locale': {
      handler() {
        this.init();
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('point', ['pointInfo']),
    ...mapGetters('exchange', ['exchangeCurrency', 'isNjPointWithdraw']),
    ...mapGetters(['serviceUrl']),
    userRegion() {
      return checkUserRegion();
    },
    exchangeRate() {
      return this.exchangeCurrency.exchangeRate;
    },
    currencyMark() {
      if (this.isNjPointWithdraw) {
        return 'P';
      }

      switch (this.exchangeCurrency.currency) {
        case 'USD':
          return '$';
        case 'KRW':
          return '₩';
        case 'JPY':
          return '¥';
        default:
          return '$';
      }
    },
    currentWithdrawValue() {
      return this.removeComma(this.point) / this.exchangeRate;
    },
    warningMessageValidatePoint() {
      return () => {
        const inputPoint = this.removeComma(this.point);
        const unit = this.pointInfo.pointUnitName;
        const { exchangeAmountUnit, exchangeMaxAmount, exchangeMinAmount } = this.exchangeCurrency;

        if (inputPoint === '') {
          return undefined;
        }

        if (inputPoint < exchangeMinAmount) {
          return `! ${this.$t('global.validation.minWithdrawPoint', {
            minValue: this.addComma(exchangeMinAmount),
            unit,
          })}`;
        }

        if (inputPoint > exchangeMaxAmount) {
          return `! ${this.$t('global.validation.maxWithdrawPoint', {
            maxValue: this.addComma(exchangeMaxAmount),
            unit,
          })}`;
        }

        if (inputPoint % exchangeAmountUnit !== 0) {
          return `! ${this.$t('global.validation.amountUnit', { unitValue: this.addComma(exchangeAmountUnit), unit })}`;
        }

        if (inputPoint > this.pointInfo.point) {
          return `! ${this.$t('global.validation.notHasEnoughPoint')}`;
        }
      };
    },
    guideURL() {
      return GLOBAL_GUIDE_URL[this.$i18n.locale];
    },
  },
  methods: {
    ...mapActions('modal', ['openModal']),
    ...mapActions('point', ['getPointInfo']),
    ...mapActions('exchange', ['getExchangeInfo', 'setIsNjPointWithdraw']),
    async init() {
      if (!this.pointInfo.pointName) {
        // 출금 유형 (현금/포인트)을 선택해야 해당 페이지로 진입할 수 있음.
        // 위 내용을 체크하기 위해 이전 페이지인 WithdrawInfo가 시작점이어야 함.
        this.$router.push({ name: 'GlobalWithdrawInfo', query: { ...this.$route.query } });
      }
    },
    async goToGlobalWithdraw() {
      const cashAmount = Number(this.removeComma(this.point)); // 출금 신청 포인트
      const payAmount = this.currentWithdrawValue; //출금 신청 금액

      try {
        let response;
        let tipaltiParams = {};

        if (this.isNjPointWithdraw) {
          const params = {
            cashAmount,
            currencyAmount: payAmount,
          };

          response = await exchangeApi.requestNexonPointWithdraw(params);
        } else {
          const currencyCode = this.exchangeCurrency.currency; // USD 또는 KRW
          const uiCulture = this.$i18n.locale === 'zh' ? 'en' : this.$i18n.locale; // 언어

          const query = this.$route.query;
          let redirectURL = `${process.env.VUE_APP_HOST}/withdrawComplete`;

          if (!!query && !!query['ifpf']) {
            redirectURL = `${process.env.VUE_APP_HOST}/withdrawComplete?ifpf=${query['ifpf']}`;
          }

          tipaltiParams = {
            cashAmount,
            payAmount,
            currencyCode,
            uiCulture,
            redirectURL,
          };

          response = await exchangeApi.reserveWithdraw(tipaltiParams);
        }

        const { data, code } = response.data;

        if (code !== 0) {
          this.validateTipalti(code);
          return;
        }

        if (this.isNjPointWithdraw) {
          this.$router.push({
            name: 'GlobalWithdrawResult',
            query: {
              ...this.$route.query,
              status: 'success',
            },
          });
          return;
        }

        // tipalti iframe 호출
        const { iframeURL, orderID } = data;

        localStorage.setItem(
          'tipalti',
          JSON.stringify({
            ...tipaltiParams,
            orderId: orderID, // 주문번호
            serviceUrl: this.serviceUrl,
            lang: this.$i18n.locale, // redirect 시 언어 설정을 위함
          }),
        );

        location.href = iframeURL;
      } catch (error) {
        this.openModal({
          type: 'alert',
          message: this.$t('global.validation.commonError'),
        });
      }
    },
    validateTipalti(code) {
      const unit = this.pointInfo.pointUnitName;
      const { exchangeAmountUnit, exchangeMaxAmount, exchangeMinAmount } = this.exchangeCurrency;
      // 코드표 https://confluence.nexon.com/pages/viewpage.action?pageId=854931972
      switch (code) {
        case 3005: // 출금 횟수 초과 rp core
        case 80014: // 출금 횟수 초과 ssonda
          this.openModal({
            type: 'alert',
            message: this.$t('global.notAvailableWithdrawCount'),
          });
          break;
        case 3004: // 잔액 부족
        case 3006: // 잔액 부족
          this.openModal({
            type: 'alert',
            message: this.$t('global.notAvailableEnoughPoint'),
          });
          break;
        case 3013: // 화폐 최소 금액 이하일 때
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.minWithdrawPoint', {
              minValue: this.addComma(exchangeMinAmount),
              unit,
            }),
          });
          break;
        case 3014: // 화폐 최대 금액 이하일 때
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.maxWithdrawPoint', {
              maxValue: this.addComma(exchangeMaxAmount),
              unit,
            }),
          });
          break;
        case 80011: // 신청 단위가 잘못 되었을 때
        case 3012: // 신청 단위가 잘못 되었을 때
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.amountUnit', {
              unitValue: this.addComma(exchangeAmountUnit),
              unit,
            }),
          });
          break;
        case 3010:
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.notAvailableApplication'),
          });
          break;
        case 3011:
          this.openModal({
            type: 'alert',
            message: this.$t('global.notAvailableEnoughPoint'),
          });
          break;
        case 3015:
          this.openModal({
            type: 'alert',
            message: this.$t('global.notAvailablePeriod'),
          });
          break;
        case 80012:
        case 80013:
        case 80020:
        case 80021:
        case 6001:
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.commonError'),
          });
          break;

        default:
          this.openModal({
            type: 'alert',
            message: this.$t('global.validation.commonError'),
          });
          break;
      }
    },
    handleCheck() {
      this.agreeCheck = !this.agreeCheck;
    },
    setPointToMax() {
      this.point = this.addComma(Math.min(this.exchangeCurrency.exchangeMaxAmount, this.pointInfo.point));
    },
  },
};
</script>

<style lang="scss" scoped>
hr.solid {
  width: 100%;
  margin: 24px 0 30px 0;
  border-top: 1px solid #ddd;
}
.btn04 {
  margin-right: 10px;
}
.warning {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  text-align: right;
  color: #d83d3f;
  margin: 8px 67px 0 0;
}
.input_wrapper {
  display: flex;
  margin-right: 30px;
}

.withdraw_form_wrapper {
  display: flex;
  flex-direction: column;
}
.content_withdraw_title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.content_withdraw_form {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.content_withdraw_point_form {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.content_withdraw_form_title {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: #111111;
}
.content_withdraw_own_point {
  display: flex;
  align-items: center;
}
.content_withdraw_own_point_value {
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  color: #333333;
  margin-right: 30px;
}
.content_withdraw_own_point_title {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
}
.point_per_currency_wrapper {
  background: rgba(10, 116, 255, 0.06);
  border-radius: 61.5px;
  padding: 10px;
}
.point_per_currency {
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  color: #0a74ff;
}
.h3 {
  font-family: 'NEXONLv1Gothic';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  color: #111111;
}
.withdraw_box {
  display: flex;
  justify-content: space-between;
  background: #f2f3fc;
  border: 1px solid #e1e3f0;
  padding: 40px 30px;
  margin-top: 30px;
}
.withdraw_box_title {
  font-family: 'NEXONLv1Gothic';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  color: #333333;
}
.withdraw_box_description {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #666666;
  margin-top: 12px;
}
.withdraw_box_value {
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 43px;
  color: #333333;
}
.checkbox_wrapper {
  margin-top: 30px;
}

.input_check {
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 17px;
  color: #333333;
}
.caution {
  margin: 20px 0 0 30px;
  .caution_description {
    list-style: none;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    margin-top: 10px;
    color: #999999;
    &.jppc {
      margin-top: 0;
    }
  }
}
.caution_guide {
  max-width: 502px;
  align-self: center;
  text-align: center;
  margin-bottom: 30px;
  color: #666;
}
.btn01 {
  width: 180px;
  align-self: center;
}

.caution_anchor {
  color: #111111;
  white-space: nowrap;
}
.caution_anchor::after {
  content: '';
  display: inline-block;
  background: url('~@/assets/images/icon_right_arrow.png') no-repeat;
  // background-size: 40px 40px;
  width: 5px;
  height: 9px;
  vertical-align: middle;
  margin-left: 7px;
}
.small_title {
  display: none;
}
.point_input input {
  text-align: right;
  font-size: 15px;
}
.point_input input::placeholder {
  color: #cccccc;
  font-size: 13px;
}

@media screen and (max-width: 768px) {
  .caution {
    margin: 20px 0 0 0;
  }
  .content_withdraw_form {
    flex-direction: column;
    align-items: start;
  }
  .content_withdraw_point_form {
    flex-direction: column;
    align-items: start;
  }
  .content_withdraw_own_point {
    margin-top: 10px;
  }
  .content_withdraw_own_point_value {
    margin-right: 10px;
  }
  .warning {
    text-align: left;
  }
  .input_wrapper {
    margin-right: 10px;
  }
  .withdraw_box {
    flex-direction: column;
    align-items: start;
    padding: 30px 15px;
  }
  .withdraw_box_value {
    margin-top: 30px;
  }
}

@media screen and (max-width: 480px) {
  .input_wrapper {
    flex-direction: column;
    width: 100%;
  }
  .point_input {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    .input01 {
      width: 100%;
      margin-right: 10px;
    }
  }
  .content_withdraw_own_point {
    width: 100%;
  }
  .content_withdraw_own_point_title {
    display: none;
  }
  .small_title {
    display: inline;
    white-space: nowrap;
    align-self: center;
  }
}
</style>
